// Measurement tools using MapLibre + Turf
import { useEffect, useRef } from 'react';
import type { Map as MaplibreMap, Marker } from 'maplibre-gl';
import { distance, area as turfArea, lineString, polygon as turfPolygon } from '@turf/turf';

const SOURCE_ID = 'measure-source';
const LINE_LAYER_ID = 'measure-line';
const FILL_LAYER_ID = 'measure-fill';

type MeasureMode = 'distance' | 'area' | null;

interface MeasureResult {
  type: 'distance' | 'area';
  value: number;
  points: [number, number][];
}

interface MeasureToolsProps {
  map: MaplibreMap | null;
  active: MeasureMode;
  onResult: (result: MeasureResult | null) => void;
}

export function MeasureTools({ map, active, onResult }: MeasureToolsProps) {
  const markersRef = useRef<Marker[]>([]);
  const pointsRef = useRef<[number, number][]>([]);

  useEffect(() => {
    if (!map || !active) {
      if (map) cleanup(map);
      onResult(null);
      return;
    }

    // Add source/layers if not exists
    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] } as any,
      });
    }
    if (!map.getLayer(LINE_LAYER_ID)) {
      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': active === 'distance' ? '#1e40af' : '#6b21a8',
          'line-width': 3,
          'line-opacity': 0.8,
        },
      });
    }
    if (active === 'area' && !map.getLayer(FILL_LAYER_ID)) {
      map.addLayer({
        id: FILL_LAYER_ID,
        type: 'fill',
        source: SOURCE_ID,
        paint: {
          'fill-color': '#9333ea',
          'fill-opacity': 0.2,
        },
      });
    }

    const clickHandler = (e: any) => {
      const { lng, lat } = e.lngLat;
      addPoint(map, [lng, lat], markersRef, pointsRef, active);
      updateShape(map, pointsRef.current, active);
      calculateResult(pointsRef.current, active, onResult);
    };
    map.on('click', clickHandler);

    return () => {
      map.off('click', clickHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, active]);

  return null;
}

function cleanup(m: MaplibreMap) {
  // We can't access markersRef from here — handled by effect cleanup
  if (m.getLayer(LINE_LAYER_ID)) m.removeLayer(LINE_LAYER_ID);
  if (m.getLayer(FILL_LAYER_ID)) m.removeLayer(FILL_LAYER_ID);
  if (m.getSource(SOURCE_ID)) m.removeSource(SOURCE_ID);
}

function addPoint(
  m: MaplibreMap,
  pt: [number, number],
  markersRef: React.MutableRefObject<Marker[]>,
  pointsRef: React.MutableRefObject<[number, number][]>,
  mode: 'distance' | 'area'
) {
  pointsRef.current.push(pt);
  // Use MapLibre's built-in Marker
  import('maplibre-gl').then(({ Marker }) => {
    const marker = new Marker({ color: mode === 'distance' ? '#1e40af' : '#6b21a8' })
      .setLngLat(pt)
      .addTo(m);
    markersRef.current.push(marker);
  });
}

function updateShape(m: MaplibreMap, points: [number, number][], mode: 'distance' | 'area') {
  const source = m.getSource(SOURCE_ID) as any;
  if (!source) return;

  if (points.length < 2) return;

  if (mode === 'distance') {
    source.setData({ type: 'Feature', geometry: lineString(points).geometry, properties: {} } as any);
  } else if (mode === 'area' && points.length >= 3) {
    const poly = turfPolygon([[...points, points[0]]]);
    source.setData({ type: 'Feature', geometry: poly.geometry, properties: {} } as any);
  }
}

function calculateResult(
  points: [number, number][],
  mode: 'distance' | 'area',
  onResult: (r: MeasureResult | null) => void
) {
  if (points.length < 2) {
    onResult(null);
    return;
  }
  if (mode === 'distance') {
    let total = 0;
    for (let i = 0; i < points.length - 1; i++) {
      total += distance(points[i], points[i + 1], { units: 'meters' });
    }
    onResult({ type: 'distance', value: total, points: [...points] });
  } else if (mode === 'area' && points.length >= 3) {
    const poly = turfPolygon([[...points, points[0]]]);
    const sqm = turfArea(poly);
    onResult({ type: 'area', value: sqm, points: [...points] });
  }
}

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${meters.toFixed(0)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

export function formatArea(sqm: number): string {
  if (sqm < 10_000) return `${sqm.toFixed(0)} m²`;
  return `${(sqm / 10_000).toFixed(2)} ha`;
}
